import type { ViewCatalogItem } from "./types";

export type OpenSNSeoArtifacts = {
  structuredData: Record<string, unknown>;
  crawlableHtml: string;
};

export type OpenSNSeoOptions = {
  url: string | URL;
  baseUrl?: string | URL;
  localeCode?: string;
  view: ViewCatalogItem;
  initialData: Record<string, unknown>;
  pageTitle: string;
  siteName?: string;
  primaryEndpoint?: string;
  filter?: string;
  currentQuery?: Record<string, string>;
  profileIdentifier?: string;
};

export type OpenSNInitialDataEndpointOptions = Pick<OpenSNSeoOptions, "url" | "baseUrl" | "view" | "primaryEndpoint" | "filter" | "currentQuery" | "profileIdentifier">;
export type OpenSNInitialDataEndpointRouteOptions = Omit<OpenSNInitialDataEndpointOptions, "view"> & {
  view?: ViewCatalogItem | null;
};
export type OpenSNSeoRouteOptions = Omit<OpenSNSeoOptions, "view" | "pageTitle"> & {
  view?: ViewCatalogItem | null;
  pageTitle?: string;
};

const OPEN_SN_CORE_ROUTE_VIEWS = {
  feed: { name: "Home Feed", slug: "home-feed", module: "feed", layoutTemplate: "social-shell-feed", primaryAction: null, endpoint: "/api/posts" },
  profile: { name: "Profile Overview", slug: "profile-overview", module: "profile", layoutTemplate: "profile-page", primaryAction: null, endpoint: "/api/profile" },
  post: { name: "Post Detail", slug: "post-detail", module: "feed", layoutTemplate: "social-shell-feed", primaryAction: null, endpoint: "/api/posts" },
  search: { name: "Search", slug: "search", module: "search", layoutTemplate: "grid-directory", primaryAction: null, endpoint: "/api/search" },
  saved: { name: "Saved Items", slug: "saved-items", module: "saved", layoutTemplate: "social-shell-feed", primaryAction: null, endpoint: "/api/bookmarks" },
  network: { name: "Network", slug: "network", module: "network", layoutTemplate: "grid-directory", primaryAction: null, endpoint: "/api/follows" },
  chat: { name: "Chat", slug: "chat", module: "chat", layoutTemplate: "chat-shell", primaryAction: "Send", endpoint: "/api/chat/rooms" },
  notifications: { name: "Notifications", slug: "notifications", module: "notifications", layoutTemplate: "social-shell-feed", primaryAction: "Mark all read", endpoint: "/api/notifications" },
  support: { name: "Support/Product Feedback", slug: "support-product-feedback", module: "support", layoutTemplate: "support-form-card", primaryAction: "Submit", endpoint: "/api/support/tickets" },
  supportReportAbuse: { name: "Support/Report Abuse", slug: "support-report-abuse", module: "support", layoutTemplate: "support-form-card", primaryAction: "Submit report", endpoint: "/api/support/tickets" },
  settingsProfile: { name: "Settings/Profile", slug: "settings-profile", module: "settings", layoutTemplate: "settings-split-form", primaryAction: "Save", endpoint: "/api/settings/profile" },
  settingsPrivacy: { name: "Settings/Privacy", slug: "settings-privacy", module: "settings", layoutTemplate: "settings-split-form", primaryAction: "Save", endpoint: "/api/settings/privacy" },
  settingsNotifications: { name: "Settings/Notifications", slug: "settings-notifications", module: "settings", layoutTemplate: "settings-split-form", primaryAction: "Save", endpoint: "/api/settings/notifications" },
  settingsEmail: { name: "Settings/Email", slug: "settings-email", module: "settings", layoutTemplate: "settings-split-form", primaryAction: "Save", endpoint: "/api/settings/email" },
  settingsPassword: { name: "Settings/Password", slug: "settings-password", module: "settings", layoutTemplate: "settings-split-form", primaryAction: "Save", endpoint: "/api/settings/password" },
  settingsTwoFactor: { name: "Settings/Two Factor", slug: "settings-two-factor", module: "settings", layoutTemplate: "settings-split-form", primaryAction: "Save", endpoint: "/api/auth/two-factor/setup" },
  settingsDisplay: { name: "Settings/Display", slug: "settings-display", module: "settings", layoutTemplate: "settings-split-form", primaryAction: "Save", endpoint: "/api/me/settings" },
  settingsDataDownload: { name: "Settings/Data Download", slug: "settings-data-download", module: "settings", layoutTemplate: "settings-split-form", primaryAction: "Submit", endpoint: "/api/settings/data-export" },
  settingsAccount: { name: "Settings/Account", slug: "settings-account", module: "settings", layoutTemplate: "settings-split-form", primaryAction: "Delete account", endpoint: "/api/settings/account-delete" },
  clips: { name: "Clips", slug: "clips", module: "clips", layoutTemplate: "clip-vertical", primaryAction: null, endpoint: "/api/clips/feed" },
  clipsCreate: { name: "Clips Create", slug: "clips-create", module: "clips", layoutTemplate: "simple-form-card", primaryAction: "Create clip", endpoint: "/api/clips" },
  articles: { name: "Articles", slug: "articles", module: "articles", layoutTemplate: "grid-directory", primaryAction: null, endpoint: "/api/articles" },
  articlesCreate: { name: "Article Editor", slug: "article-editor", module: "articles", layoutTemplate: "simple-form-card", primaryAction: "Create article", endpoint: "/api/articles" },
  groups: { name: "Groups", slug: "groups", module: "groups", layoutTemplate: "grid-directory", primaryAction: null, endpoint: "/api/groups" },
  groupsCreate: { name: "Groups Create", slug: "groups-create", module: "groups", layoutTemplate: "simple-form-card", primaryAction: "Create group", endpoint: "/api/groups" },
  events: { name: "Events", slug: "events", module: "events", layoutTemplate: "grid-directory", primaryAction: null, endpoint: "/api/events" },
  eventsCreate: { name: "Events Create", slug: "events-create", module: "events", layoutTemplate: "simple-form-card", primaryAction: "Create event", endpoint: "/api/events" },
  pages: { name: "Pages", slug: "pages", module: "pages", layoutTemplate: "grid-directory", primaryAction: null, endpoint: "/api/pages" },
  pagesCreate: { name: "Pages Create", slug: "pages-create", module: "pages", layoutTemplate: "simple-form-card", primaryAction: "Create page", endpoint: "/api/pages" },
  documents: { name: "Documents", slug: "documents", module: "documents", layoutTemplate: "grid-directory", primaryAction: null, endpoint: "/api/documents" },
  documentsCreate: { name: "Document Create", slug: "document-create", module: "documents", layoutTemplate: "simple-form-card", primaryAction: "Create document", endpoint: "/api/documents" },
  ads: { name: "Ads", slug: "ads", module: "ads", layoutTemplate: "ads-manager", primaryAction: null, endpoint: "/api/ads" },
  adsCreate: { name: "Ads Create", slug: "ads-create", module: "ads", layoutTemplate: "ads-manager", primaryAction: null, endpoint: "/api/ads" },
  adsReport: { name: "Ads Report", slug: "ads-report", module: "ads", layoutTemplate: "ads-manager", primaryAction: null, endpoint: "/api/ads" },
  invites: { name: "Invites Overview", slug: "invites-overview", module: "network", layoutTemplate: "social-shell-feed", primaryAction: "Send invite", endpoint: "/api/invites" },
  invite: { name: "Invite", slug: "invite", module: "network", layoutTemplate: "simple-form-card", primaryAction: "Send invite", endpoint: "/api/invites" },
  membership: { name: "Membership Plans", slug: "membership-plans", module: "membership", layoutTemplate: "wallet-membership", primaryAction: "Select plan", endpoint: "/api/membership/plans" },
  membershipCurrent: { name: "Membership Current", slug: "membership-current", module: "membership", layoutTemplate: "wallet-membership", primaryAction: null, endpoint: "/api/membership/current" },
  membershipCheckout: { name: "Membership Checkout", slug: "membership-checkout", module: "membership", layoutTemplate: "wallet-membership", primaryAction: "Start checkout", endpoint: "/api/membership/plans" },
  wallet: { name: "Wallet", slug: "wallet", module: "wallet", layoutTemplate: "wallet-membership", primaryAction: "Add funds", endpoint: "/api/wallet/balance" },
  walletDeposit: { name: "Wallet Deposit", slug: "wallet-deposit", module: "wallet", layoutTemplate: "wallet-membership", primaryAction: "Add funds", endpoint: "/api/wallet/deposit" },
  walletWithdraw: { name: "Wallet Withdraw", slug: "wallet-withdraw", module: "wallet", layoutTemplate: "wallet-membership", primaryAction: "Withdraw", endpoint: "/api/wallet/withdraw" },
  walletTransfer: { name: "Wallet Transfer", slug: "wallet-transfer", module: "wallet", layoutTemplate: "wallet-membership", primaryAction: "Transfer", endpoint: "/api/wallet/transfer" },
  walletTransactions: { name: "Wallet Transactions", slug: "wallet-transactions", module: "wallet", layoutTemplate: "wallet-membership", primaryAction: null, endpoint: "/api/wallet" },
  ops: { name: "Ops Overview", slug: "ops-overview", module: "ops", layoutTemplate: "ops-table", primaryAction: null, endpoint: "/api/ops/overview" },
  opsUsers: { name: "Ops Users", slug: "ops-users", module: "ops", layoutTemplate: "ops-table", primaryAction: null, endpoint: "/api/ops/users" }
} as const;

type OpenSNCoreRouteView = (typeof OPEN_SN_CORE_ROUTE_VIEWS)[keyof typeof OPEN_SN_CORE_ROUTE_VIEWS];
type OpenSNProfileTab = "about" | "friends" | "photos" | "articles";

const OPEN_SN_PROFILE_TAB_TITLES: Record<"en" | "ro", Record<OpenSNProfileTab, string>> = {
  en: { about: "About", friends: "Friends", photos: "Photos", articles: "Articles" },
  ro: { about: "Despre", friends: "Prieteni", photos: "Fotografii", articles: "Articole" }
};

const OPEN_SN_BREADCRUMB_SECTION_TITLES: Record<"en" | "ro", Record<string, string>> = {
  en: {
    timeline: "Home",
    post: "Posts",
    search: "Search",
    hashtags: "Hashtags",
    saved: "Saved",
    friends: "Friends",
    network: "Friends",
    users: "Friends",
    clips: "Video",
    articles: "Articles",
    groups: "Groups",
    events: "Events",
    pages: "Pages",
    documents: "Documents",
    notifications: "Notifications",
    chat: "Messenger",
    settings: "Settings",
    invites: "Invites",
    membership: "Membership",
    subscription: "Membership",
    wallet: "Wallet",
    support: "Support",
    admin: "Admin",
    ops: "Admin"
  },
  ro: {
    timeline: "Acasa",
    post: "Postari",
    search: "Cautare",
    hashtags: "Hashtaguri",
    saved: "Salvate",
    friends: "Prieteni",
    network: "Prieteni",
    users: "Prieteni",
    clips: "Video",
    articles: "Articole",
    groups: "Grupuri",
    events: "Evenimente",
    pages: "Pagini",
    documents: "Documente",
    notifications: "Notificari",
    chat: "Mesaje",
    settings: "Setari",
    invites: "Invitatii",
    membership: "Abonament",
    subscription: "Abonament",
    wallet: "Portofel",
    support: "Suport",
    admin: "Admin",
    ops: "Admin"
  }
};

const OPEN_SN_ROUTE_VIEW_TITLES: Record<"en" | "ro", Record<string, string>> = {
  en: {
    "home-feed": "Home",
    "profile-overview": "Profile",
    "post-detail": "Post",
    search: "Search",
    "saved-items": "Saved",
    network: "Friends",
    chat: "Messenger",
    notifications: "Notifications",
    clips: "Video",
    articles: "Articles",
    groups: "Groups",
    events: "Events",
    pages: "Pages",
    documents: "Documents",
    ads: "Ads",
    "invites-overview": "Invites",
    invite: "Invite",
    "membership-plans": "Membership",
    "membership-current": "Membership",
    "membership-checkout": "Checkout",
    wallet: "Wallet",
    "wallet-deposit": "Deposit",
    "wallet-withdraw": "Withdraw",
    "wallet-transfer": "Transfer",
    "wallet-transactions": "Transactions",
    "ops-overview": "Admin",
    "ops-users": "Users"
  },
  ro: {
    "home-feed": "Acasa",
    "profile-overview": "Profil",
    "post-detail": "Postare",
    search: "Cautare",
    "saved-items": "Salvate",
    network: "Prieteni",
    chat: "Mesaje",
    notifications: "Notificari",
    clips: "Video",
    articles: "Articole",
    groups: "Grupuri",
    events: "Evenimente",
    pages: "Pagini",
    documents: "Documente",
    ads: "Reclame",
    "invites-overview": "Invitatii",
    invite: "Invitatie",
    "membership-plans": "Abonament",
    "membership-current": "Abonament",
    "membership-checkout": "Plata",
    wallet: "Portofel",
    "wallet-deposit": "Depunere",
    "wallet-withdraw": "Retragere",
    "wallet-transfer": "Transfer",
    "wallet-transactions": "Tranzactii",
    "ops-overview": "Admin",
    "ops-users": "Utilizatori"
  }
};

export function openSNCoreViewForPath(value: string | URL): ViewCatalogItem | null {
  const path = routePathname(value);
  if (path === "/" || path === "/timeline") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.feed);
  if (path.startsWith("/@") || path === "/profile" || path.startsWith("/profile/")) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.profile);
  if (path.startsWith("/post/")) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.post);
  if (path === "/search") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.search);
  if (path === "/hashtags" || path.startsWith("/hashtags/")) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.search);
  if (path === "/saved") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.saved);
  if (path === "/friends" || path === "/network" || path === "/users") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.network);
  if (path === "/chat" || path.startsWith("/chat/")) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.chat);
  if (path === "/notifications") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.notifications);
  if (path === "/support/report-abuse") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.supportReportAbuse);
  if (path.startsWith("/support")) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.support);
  if (path === "/settings/password") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.settingsPassword);
  if (path === "/settings/privacy") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.settingsPrivacy);
  if (path === "/settings/notifications") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.settingsNotifications);
  if (path === "/settings/email") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.settingsEmail);
  if (path === "/settings/two_factor" || path === "/settings/two-factor") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.settingsTwoFactor);
  if (path === "/settings/display") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.settingsDisplay);
  if (path === "/settings/download") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.settingsDataDownload);
  if (path === "/settings/account") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.settingsAccount);
  if (path.startsWith("/settings")) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.settingsProfile);
  if (path === "/clips/create") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.clipsCreate);
  if (path === "/clips" || path.startsWith("/clips/")) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.clips);
  if (path === "/articles/editor" || path === "/articles/create") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.articlesCreate);
  if (path === "/articles" || path.startsWith("/articles/")) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.articles);
  if (path === "/groups/create") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.groupsCreate);
  if (path === "/groups" || path.startsWith("/groups/")) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.groups);
  if (path === "/events/create") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.eventsCreate);
  if (path === "/events" || path.startsWith("/events/")) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.events);
  if (path === "/pages/create") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.pagesCreate);
  if (path === "/pages" || path.startsWith("/pages/")) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.pages);
  if (path === "/documents/create") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.documentsCreate);
  if (path === "/documents" || path.startsWith("/documents/")) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.documents);
  if (path === "/ads/create") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.adsCreate);
  if (/^\/ads\/[^/]+\/report$/u.test(path)) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.adsReport);
  if (path === "/ads" || path.startsWith("/ads/")) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.ads);
  if (path === "/invites/invite") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.invite);
  if (path === "/invites" || path.startsWith("/invites/")) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.invites);
  if (path === "/subscription") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.membershipCurrent);
  if (path === "/membership/current") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.membershipCurrent);
  if (path === "/membership/checkout") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.membershipCheckout);
  if (path === "/membership" || path.startsWith("/membership/")) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.membership);
  if (path === "/wallet/deposit") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.walletDeposit);
  if (path === "/wallet/withdraw") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.walletWithdraw);
  if (path === "/wallet/transfer") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.walletTransfer);
  if (path === "/wallet/transactions") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.walletTransactions);
  if (path === "/wallet" || path.startsWith("/wallet/")) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.wallet);
  if (path === "/admin/users" || path === "/ops/users") return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.opsUsers);
  if (path === "/admin" || path.startsWith("/admin/") || path === "/ops" || path.startsWith("/ops/")) return openSNCoreViewCatalogItem(OPEN_SN_CORE_ROUTE_VIEWS.ops);
  return null;
}

function routePathname(value: string | URL): string {
  if (value instanceof URL) return normalizeAppPath(value.pathname);
  const raw = String(value ?? "");
  try {
    if (/^[a-z][a-z0-9+.-]*:\/\//iu.test(raw)) return normalizeAppPath(new URL(raw).pathname);
  } catch {
  }
  return normalizeAppPath(raw);
}

function openSNCoreViewCatalogItem(view: OpenSNCoreRouteView): ViewCatalogItem {
  return {
    ...view,
    title: view.name,
    component: "ContractView",
    actions: view.primaryAction ? [view.primaryAction] : []
  };
}

export function createOpenSNSeoArtifacts(options: OpenSNSeoOptions): OpenSNSeoArtifacts {
  const resolvedOptions = routeAwareSeoOptions(options);
  return {
    structuredData: createOpenSNStructuredData(resolvedOptions),
    crawlableHtml: createOpenSNCrawlableHtml(resolvedOptions)
  };
}

export function createOpenSNSeoArtifactsForRoute(options: OpenSNSeoRouteOptions): OpenSNSeoArtifacts | null {
  const view = resolveOpenSNRouteView(options.url, options.view);
  if (!view) return null;
  return createOpenSNSeoArtifacts({
    ...options,
    view,
    pageTitle: resolvedRoutePageTitle(options, view)
  });
}

export function createOpenSNStructuredData(options: OpenSNSeoOptions): Record<string, unknown> {
  options = routeAwareSeoOptions(options);
  const url = asUrl(options.url, options.baseUrl);
  const siteName = displayText(options.siteName, "OpenSN");
  const pageUrl = pageAbsoluteUrl(url);
  const payload = primaryInitialPayload(options);
  const rows = payloadRows(payload);
  const listEndpoint = primaryListEndpoint(options);
  const listView = listEndpoint ? semanticViewForEndpoint(options.view, listEndpoint) : options.view;
  const isDetailPage = listEndpoint ? isStructuredDetailView(options.view, listEndpoint) : false;
  const hasStructuredList = !isDetailPage && Boolean(rows.length || (listEndpoint && isStructuredListView(options.view, listEndpoint)));
  const itemsId = `${pageUrl}#items`;
  const profile = seoPageProfileRow(options, listEndpoint);
  const detailItem = isDetailPage && rows[0] ? structuredListItem(url, listView, rows[0], profile, siteName) : null;
  const detailItemId = detailItem ? structuredEntityId(detailItem) : "";
  const profileUrl = profile ? absoluteUrl(url, publicProfilePath(profile)) : "";
  const profileEntityId = profileUrl ? `${profileUrl}#person` : "";
  const pageMainEntity = detailItemId ? { "@id": detailItemId } : hasStructuredList ? { "@id": itemsId } : profileEntityId ? { "@id": profileEntityId } : undefined;
  const breadcrumbs = structuredBreadcrumbItems(url, options, rows, profile, siteName);
  const breadcrumbId = `${pageUrl}#breadcrumb`;
  const graph: Array<Record<string, unknown>> = [
    { "@type": "WebSite", "@id": `${url.origin}/#website`, "name": siteName, "url": url.origin, "potentialAction": { "@type": "SearchAction", "target": `${url.origin}/search?q={search_term_string}`, "query-input": "required name=search_term_string" } },
    { "@type": structuredPageTypes(options.view, listEndpoint, isDetailPage, hasStructuredList), "@id": `${pageUrl}#webpage`, "name": options.pageTitle, "url": pageUrl, "isPartOf": { "@id": `${url.origin}/#website` }, "mainEntity": pageMainEntity, "breadcrumb": breadcrumbs.length > 1 ? { "@id": breadcrumbId } : undefined }
  ];
  if (breadcrumbs.length > 1) {
    graph.push({
      "@type": "BreadcrumbList",
      "@id": breadcrumbId,
      "itemListElement": breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.item
      }))
    });
  }
  if (profile) {
    const profileIdentifier = publicProfileIdentifier(profile);
    graph.push({
      "@type": "Person",
      "@id": profileEntityId,
      "name": personDisplayName(profile, "Member"),
      "alternateName": profileIdentifier ? `@${profileIdentifier}` : undefined,
      "url": profileUrl,
      "image": profileImageUrl(url, profile),
      "description": displayText(profile.bio ?? profile.description ?? profile.headline, "")
    });
    graph.push({ "@type": "ProfilePage", "@id": `${profileUrl}#profile`, "name": options.pageTitle, "url": pageUrl, "mainEntity": { "@id": `${profileUrl}#person` } });
  }
  if (detailItem) graph.push(detailItem);
  if (hasStructuredList) {
    graph.push({
      "@type": "ItemList",
      "@id": itemsId,
      "name": options.pageTitle,
      "numberOfItems": payloadItemCount(payload, rows.length),
      "itemListElement": rows.map((row, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": structuredListItem(url, listView, row, profile, siteName)
      }))
    });
  }
  return { "@context": "https://schema.org", "@graph": compactJsonLd(graph) };
}

export function createOpenSNCrawlableHtml(options: OpenSNSeoOptions): string {
  options = routeAwareSeoOptions(options);
  const url = asUrl(options.url, options.baseUrl);
  const rows = primaryInitialRows(options);
  const pageTitle = displayText(options.pageTitle, "OpenSN");
  const listEndpoint = primaryListEndpoint(options);
  const profile = seoPageProfileRow(options, listEndpoint);
  const listView = listEndpoint ? semanticViewForEndpoint(options.view, listEndpoint) : options.view;
  const hasCrawlableList = Boolean(rows.length || (listEndpoint && isStructuredListView(options.view, listEndpoint)));
  if (!hasCrawlableList && !profile) return `<main aria-label="${escapeHtml(pageTitle)}"><h1>${escapeHtml(pageTitle)}</h1></main>`;
  const profileIdentifier = profile ? publicProfileIdentifier(profile) : "";
  const profileName = profile ? personDisplayName(profile, "Profile") : "";
  const profileImage = profile ? profileImageUrl(url, profile) : undefined;
  const profileHtml = profile ? `<section>${profileImage ? `<img src="${escapeHtml(profileImage)}" alt="${escapeHtml(profileName)}">` : ""}<h1>${escapeHtml(profileName)}</h1>${profileIdentifier ? `<p>@${escapeHtml(profileIdentifier)}</p>` : ""}${profile.bio || profile.description || profile.headline ? `<p>${escapeHtml(displayText(profile.bio ?? profile.description ?? profile.headline, ""))}</p>` : ""}</section>` : "";
  const rowsHtml = rows.map((row) => crawlableRowHtml(url, listView, row)).join("");
  const listHeading = profile ? `<h2>${escapeHtml(pageTitle)}</h2>` : `<h1>${escapeHtml(pageTitle)}</h1>`;
  const listHtml = hasCrawlableList ? `<section>${listHeading}${rowsHtml}</section>` : "";
  return `<main aria-label="${escapeHtml(pageTitle)}">${profileHtml}${listHtml}</main>`;
}

export function openSNInitialDataEndpointsForView(options: OpenSNInitialDataEndpointOptions): string[] {
  const url = asUrl(options.url, options.baseUrl);
  const view = routeAwareView(url, options.view);
  const path = normalizeAppPath(url.pathname);
  const currentQuery = mergedQueryRecord(url, options.currentQuery);
  const filter = displayText(options.filter, "");
  if (options.primaryEndpoint) return [options.primaryEndpoint];
  if (isMutationFormView(view)) return [];
  if (!isCrawlableInitialDataView(view)) return [];
  if (view.name === "Post Detail") return [postDetailEndpoint(path)];
  if (view.name === "Home Feed") return [feedEndpointFromQuery(currentQuery)];
  if (view.name === "Search") {
    const query = filter.trim() || displayText(currentQuery.q, "") || hashtagQueryFromPath(path);
    return [query ? `/api/search?q=${encodeURIComponent(query)}` : "/api/search"];
  }
  if (view.module === "saved") return [directoryEndpoint("/api/bookmarks", filter, currentQuery)];
  if (view.name === "Network") return [directoryEndpoint("/api/follows", filter, currentQuery)];
  if (view.module === "clips") return [view.endpoint || "/api/clips/feed"];
  if (["Invites Overview", "Your Referrals"].includes(view.name)) return [directoryEndpoint("/api/invites", filter, currentQuery)];
  if (view.name === "List Followers" || view.name === "List Following") return ["/api/follows"];
  if (view.name === "Profile Overview") {
    const identifier = displayText(options.profileIdentifier ?? profileIdentifierFromPath(path), "");
    const profileEndpoint = profileEndpointForIdentifier(identifier);
    const tabEndpoint = identifier ? profileTabEndpointFor(profileTabFromPath(path), identifier) : null;
    return tabEndpoint ? [profileEndpoint, tabEndpoint] : [profileEndpoint];
  }
  if (view.name.includes("Article")) return [directoryEntityEndpoint("/articles", "/api/articles", path) ?? directoryEndpoint("/api/articles", filter, currentQuery)];
  if (view.name.includes("Document")) return [directoryEntityEndpoint("/documents", "/api/documents", path) ?? directoryEndpoint("/api/documents", filter, currentQuery)];
  if (view.name.includes("Groups")) return [directoryEntityEndpoint("/groups", "/api/groups", path) ?? directoryEndpoint("/api/groups", filter, currentQuery)];
  if (view.name.includes("Events")) return [directoryEntityEndpoint("/events", "/api/events", path) ?? directoryEndpoint("/api/events", filter, currentQuery)];
  if (view.name.includes("Pages")) return [directoryEntityEndpoint("/pages", "/api/pages", path) ?? directoryEndpoint("/api/pages", filter, currentQuery)];
  return [view.endpoint].filter(Boolean);
}

function isCrawlableInitialDataView(view: ViewCatalogItem): boolean {
  if (view.name === "Post Detail" || view.name === "Home Feed" || view.name === "Search" || view.name === "Profile Overview") return true;
  if (view.name === "Network" || view.name === "List Followers" || view.name === "List Following") return true;
  if (view.module === "clips") return true;
  return ["feed", "search", "saved", "articles", "documents", "groups", "events", "pages", "photos", "network"].includes(view.module);
}

function isMutationFormView(view: ViewCatalogItem): boolean {
  const name = displayText(view.name, "").toLowerCase();
  return view.layoutTemplate === "simple-form-card" || view.layoutTemplate === "support-form-card" || name.includes(" create") || name.includes(" editor") || name.endsWith(" create") || name.endsWith(" editor");
}

export function openSNInitialDataEndpointsForRoute(options: OpenSNInitialDataEndpointRouteOptions): string[] {
  const view = resolveOpenSNRouteView(options.url, options.view);
  return view ? openSNInitialDataEndpointsForView({ ...options, view }) : [];
}

function resolveOpenSNRouteView(url: string | URL, view: ViewCatalogItem | null | undefined): ViewCatalogItem | null {
  const routeView = openSNCoreViewForPath(url);
  if (!routeView) return view ?? null;
  if (!view) return routeView;
  return openSNViewMatchesRoute(view, routeView) ? view : routeView;
}

function routeAwareSeoOptions(options: OpenSNSeoOptions): OpenSNSeoOptions {
  const view = routeAwareView(options.url, options.view);
  if (openSNViewMatchesRoute(options.view, view)) return options;
  const pageTitle = isDefaultPageTitleForView(options.pageTitle, options.view, options.siteName)
    ? resolvedRoutePageTitle({ ...options, view: options.view }, view)
    : options.pageTitle;
  return { ...options, view, pageTitle };
}

function routeAwareView(url: string | URL, view: ViewCatalogItem): ViewCatalogItem {
  const routeView = openSNCoreViewForPath(url);
  return routeView && !openSNViewMatchesRoute(view, routeView) ? routeView : view;
}

function openSNViewMatchesRoute(view: ViewCatalogItem, routeView: ViewCatalogItem): boolean {
  return view.name === routeView.name || view.slug === routeView.slug;
}

function resolvedRoutePageTitle(options: OpenSNSeoRouteOptions, view: ViewCatalogItem): string {
  const explicitTitle = displayText(options.pageTitle, "");
  const dataTitle = routeDataPageTitle({ ...options, view });
  const defaultTitle = localizedRouteViewTitle(view, options.localeCode);
  if (!explicitTitle) return dataTitle || defaultTitle;
  if (isDefaultPageTitleForView(explicitTitle, view, options.siteName)) {
    return dataTitle || defaultTitle;
  }
  if (options.view && !openSNViewMatchesRoute(options.view, view) && isDefaultPageTitleForView(explicitTitle, options.view, options.siteName)) {
    return dataTitle || defaultTitle;
  }
  return explicitTitle;
}

function localizedRouteViewTitle(view: ViewCatalogItem, localeCode: unknown): string {
  const titles = OPEN_SN_ROUTE_VIEW_TITLES[seoLocaleLanguage(localeCode)];
  return displayText(titles[view.slug] ?? view.title ?? view.name, "OpenSN");
}

function routeDataPageTitle(options: OpenSNSeoRouteOptions & { view: ViewCatalogItem }): string {
  if (options.view.name === "Profile Overview") {
    const path = asUrl(options.url, options.baseUrl).pathname;
    const profile = firstProfileRow(options.initialData);
    const name = profile ? personDisplayName(profile, "") : "";
    const tabTitle = profileTabTitleFromPath(path, options.localeCode);
    if (name) return tabTitle ? `${name} - ${tabTitle}` : name;
    const identifier = displayText(options.profileIdentifier ?? profileIdentifierFromPath(normalizeAppPath(path)), "");
    return identifier && tabTitle ? `@${identifier} - ${tabTitle}` : identifier ? `@${identifier}` : tabTitle;
  }
  const endpoint = primaryListEndpoint(options);
  if (!endpoint || !isStructuredDetailView(options.view, endpoint)) return "";
  const row = primaryInitialRows(options)[0];
  if (!row) return "";
  if (options.view.module === "articles") return displayText(row.title ?? row.name, "");
  if (options.view.module === "documents") return documentRowTitle(row, "");
  if (options.view.module === "events" || options.view.module === "groups" || options.view.module === "pages") return displayText(row.title ?? row.name ?? row.slug, "");
  if (options.view.name === "Post Detail") return compactPageTitle(row.body ?? row.title ?? row.description ?? row.caption ?? authorDisplayName(row, ""), 90);
  return "";
}

function compactPageTitle(value: unknown, limit: number): string {
  const text = displayText(value, "").replace(/\s+/gu, " ");
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trimEnd()}...`;
}

function profileTabTitleFromPath(pathname: string, localeCode?: string): string {
  const tab = profileTabFromPath(normalizeAppPath(pathname));
  if (!isProfileTab(tab)) return "";
  return OPEN_SN_PROFILE_TAB_TITLES[seoLocaleLanguage(localeCode)][tab];
}

function isProfileTab(value: string): value is OpenSNProfileTab {
  return value === "about" || value === "friends" || value === "photos" || value === "articles";
}

function seoLocaleLanguage(localeCode: unknown): "en" | "ro" {
  const language = String(localeCode ?? "").trim().split(/[-_]/u)[0]?.toLowerCase();
  return language === "ro" ? "ro" : "en";
}

function isDefaultPageTitleForView(pageTitle: string, view: ViewCatalogItem, siteName: unknown): boolean {
  const normalized = normalizeTitleText(pageTitle);
  const site = normalizeTitleText(siteName);
  const viewTitles = [view.title, view.name, view.name.replace(/\//gu, " ")].map(normalizeTitleText).filter(Boolean);
  if (site && normalized === site) return true;
  return viewTitles.some((title) => normalized === title || (site && (normalized === `${title} | ${site}` || normalized === `${title} - ${site}`)));
}

function normalizeTitleText(value: unknown): string {
  return String(value ?? "").trim().replace(/\s+/gu, " ").toLowerCase();
}

export function primaryInitialRows(options: Pick<OpenSNSeoOptions, "url" | "baseUrl" | "view" | "initialData" | "primaryEndpoint" | "filter" | "currentQuery" | "profileIdentifier">): Array<Record<string, unknown>> {
  return payloadRows(primaryInitialPayload(options));
}

function primaryInitialPayload(options: Pick<OpenSNSeoOptions, "url" | "baseUrl" | "view" | "initialData" | "primaryEndpoint" | "filter" | "currentQuery" | "profileIdentifier">): unknown {
  const endpoint = primaryListEndpoint(options);
  return endpoint ? openSNInitialDataPayload(options.initialData, endpoint) : undefined;
}

function primaryListEndpoint(options: Pick<OpenSNSeoOptions, "url" | "baseUrl" | "view" | "initialData" | "primaryEndpoint" | "filter" | "currentQuery" | "profileIdentifier">): string | undefined {
  const endpoints = openSNInitialDataEndpointsForView(options);
  if (options.primaryEndpoint) return options.primaryEndpoint;
  if (options.view.name === "Profile Overview") {
    return endpoints.find((endpoint) => !isProfileEndpoint(endpoint));
  }
  if (endpoints[0]) return endpoints[0];
  if (isMutationFormView(options.view) || !isCrawlableInitialDataView(options.view)) return undefined;
  return options.view.endpoint || undefined;
}

export function firstProfileRow(initialData: Record<string, unknown>): Record<string, unknown> | undefined {
  const entry = Object.entries(initialData).find(([endpoint]) => isProfileEndpoint(endpoint));
  return entry ? payloadRows(entry[1])[0] : undefined;
}

function seoPageProfileRow(options: Pick<OpenSNSeoOptions, "view" | "initialData">, listEndpoint: string | undefined): Record<string, unknown> | undefined {
  if (options.view.name !== "Profile Overview" && options.view.module !== "profile" && !listEndpoint?.startsWith("/api/profile/")) return undefined;
  return firstProfileRow(options.initialData);
}

function isProfileEndpoint(endpoint: string): boolean {
  return endpoint === "/api/profile" || endpoint.startsWith("/api/profile?");
}

function isProfileEndpointPath(pathname: string): boolean {
  return pathname === "/api/profile" || pathname.startsWith("/api/profile/");
}

function isStructuredListView(view: ViewCatalogItem, endpoint: string): boolean {
  if (view.name === "Post Detail" || isProfileEndpoint(endpoint)) return false;
  if (endpoint.startsWith("/api/profile/posts") || endpoint.startsWith("/api/profile/friends") || endpoint.startsWith("/api/profile/media") || endpoint.startsWith("/api/profile/articles")) return true;
  if (view.layoutTemplate === "grid-directory" || view.layoutTemplate === "social-shell-feed" || view.layoutTemplate === "clip-vertical" || view.layoutTemplate === "feed") return true;
  return ["feed", "search", "saved", "clips", "events", "groups", "pages", "documents", "articles", "network"].includes(view.module);
}

function isStructuredDetailView(view: ViewCatalogItem, endpoint: string): boolean {
  if (view.name === "Post Detail") return true;
  const path = endpointUrl(endpoint)?.pathname ?? "";
  return /^\/api\/posts\/[^/]+$/u.test(path) || /^\/api\/(articles|documents|groups|events|pages)\/[^/]+$/u.test(path);
}

function structuredPageTypes(view: ViewCatalogItem, endpoint: string | undefined, isDetailPage: boolean, hasStructuredList: boolean): string | string[] {
  const pageType = structuredPageType(view, endpoint, isDetailPage, hasStructuredList);
  return pageType === "WebPage" ? "WebPage" : ["WebPage", pageType];
}

function structuredPageType(view: ViewCatalogItem, endpoint: string | undefined, isDetailPage: boolean, hasStructuredList: boolean): string {
  if (view.name === "Search" || view.module === "search") return "SearchResultsPage";
  if (view.name === "Profile Overview" || endpoint?.startsWith("/api/profile/")) return "ProfilePage";
  if (isDetailPage) return "ItemPage";
  if (hasStructuredList) return "CollectionPage";
  return "WebPage";
}

function structuredEntityId(item: Record<string, unknown>): string {
  return displayText(item["@id"], "");
}

function structuredBreadcrumbItems(url: URL, options: OpenSNSeoOptions, rows: Array<Record<string, unknown>>, profile: Record<string, unknown> | undefined, siteName: string): Array<{ name: string; item: string }> {
  const pageUrl = pageAbsoluteUrl(url);
  const path = normalizeAppPath(url.pathname);
  const items: Array<{ name: string; item: string }> = [{ name: siteName, item: `${url.origin}/` }];
  if (path === "/") return items;
  if (options.view.name === "Profile Overview" && profile) {
    pushBreadcrumbItem(items, personDisplayName(profile, "Profile"), absoluteUrl(url, publicProfilePath(profile)));
    const tab = profileTabFromPath(path);
    if (tab !== "timeline") pushBreadcrumbItem(items, profileTabTitleFromPath(path, options.localeCode) || displayText(options.pageTitle, "Profile"), pageUrl);
    return items;
  }
  const section = breadcrumbSectionItem(url, path, options.view, options.localeCode);
  if (section) pushBreadcrumbItem(items, section.name, section.item);
  if (items[items.length - 1]?.item !== pageUrl) {
    pushBreadcrumbItem(items, currentBreadcrumbName(url, options, rows[0]), pageUrl);
  }
  return items;
}

function pushBreadcrumbItem(items: Array<{ name: string; item: string }>, name: string, item: string): void {
  const cleanName = displayText(name, "");
  if (!cleanName || !item) return;
  const last = items[items.length - 1];
  if (last?.item === item) {
    last.name = cleanName;
    return;
  }
  items.push({ name: cleanName, item });
}

function breadcrumbSectionItem(url: URL, path: string, view: ViewCatalogItem, localeCode: unknown): { name: string; item: string } | null {
  const segment = path.split("/")[1] ?? "";
  const profileIdentifier = view.name === "Profile Overview" ? profileIdentifierFromPath(path) : "";
  if (profileIdentifier) return { name: `@${profileIdentifier}`, item: absoluteUrl(url, publicProfilePath({ username: profileIdentifier })) };
  const sections = OPEN_SN_BREADCRUMB_SECTION_TITLES[seoLocaleLanguage(localeCode)];
  const name = sections[segment] ?? displayText(view.title ?? view.name, "");
  if (!name) return null;
  const sectionPath = canonicalOpenSNPath(segment === "post" ? "/timeline" : segment ? `/${segment}` : path);
  return { name, item: absoluteUrl(url, sectionPath) };
}

function currentBreadcrumbName(url: URL, options: OpenSNSeoOptions, row: Record<string, unknown> | undefined): string {
  const detailName = row ? structuredDetailBreadcrumbName(options.view, row) : "";
  if (detailName) return detailName;
  if (options.view.name === "Search" || options.view.module === "search") {
    const query = displayText(url.searchParams.get("q"), "");
    if (query) return query;
  }
  return displayText(options.pageTitle || options.view.title || options.view.name, "OpenSN");
}

function structuredDetailBreadcrumbName(view: ViewCatalogItem, row: Record<string, unknown>): string {
  if (view.name === "Post Detail") return compactPageTitle(row.body ?? row.title ?? row.description ?? row.caption ?? authorDisplayName(row, ""), 70);
  if (view.module === "articles") return displayText(row.title ?? row.name, "");
  if (view.module === "documents") return documentRowTitle(row, "");
  if (view.module === "events" || view.module === "groups" || view.module === "pages") return displayText(row.title ?? row.name ?? row.slug, "");
  return "";
}

function semanticViewForEndpoint(view: ViewCatalogItem, endpoint: string): ViewCatalogItem {
  if (endpoint.startsWith("/api/profile/friends")) return { ...view, name: "Profile Friends", slug: "profile-friends", module: "network", layoutTemplate: "grid-directory", endpoint };
  if (endpoint.startsWith("/api/profile/media")) return { ...view, name: "Profile Photos", slug: "profile-photos", module: "photos", layoutTemplate: "grid-directory", endpoint };
  if (endpoint.startsWith("/api/profile/articles")) return { ...view, name: "Profile Articles", slug: "profile-articles", module: "articles", layoutTemplate: "grid-directory", endpoint };
  if (endpoint.startsWith("/api/profile/posts")) return { ...view, name: "Profile Timeline", slug: "profile-timeline", module: "feed", layoutTemplate: "feed", endpoint };
  return { ...view, endpoint };
}

function structuredListItem(url: URL, view: ViewCatalogItem, row: Record<string, unknown>, profile: Record<string, unknown> | undefined, siteName: string): Record<string, unknown> {
  if (view.module === "events" || row.starts_at || row.startsAt) {
    const href = directoryRowHref(row, "events", pageAbsoluteUrl(url));
    const onlineUrl = displayText(row.online_url ?? row.onlineUrl ?? row.event_url ?? row.eventUrl, "");
    return {
      "@type": "Event",
      "@id": `${absoluteUrl(url, href)}#event`,
      "name": displayText(row.title ?? row.name, "Event"),
      "description": displayText(row.description ?? row.body, ""),
      "url": absoluteUrl(url, href),
      "image": mediaRowUrl(url, row),
      "startDate": displayText(row.starts_at ?? row.startsAt, ""),
      "endDate": displayText(row.ends_at ?? row.endsAt, ""),
      "eventAttendanceMode": eventAttendanceMode(row, onlineUrl),
      "eventStatus": eventStatusUrl(row),
      "location": eventStructuredLocation(url, row, onlineUrl),
      "organizer": eventOrganizers(url, row)
    };
  }
  if (view.module === "groups" || view.module === "pages") {
    const href = directoryRowHref(row, view.module, pageAbsoluteUrl(url));
    return {
      "@type": "Organization",
      "@id": `${absoluteUrl(url, href)}#${view.module.slice(0, -1)}`,
      "name": displayText(row.title ?? row.name ?? row.slug, view.module === "groups" ? "Group" : "Page"),
      "description": displayText(row.description ?? row.body ?? row.excerpt, ""),
      "url": absoluteUrl(url, href),
      "image": mediaRowUrl(url, row)
    };
  }
  if (view.module === "documents") {
    const href = documentRowHref(row, pageAbsoluteUrl(url));
    return {
      "@type": "DigitalDocument",
      "@id": `${absoluteUrl(url, href)}#document`,
      "name": documentRowTitle(row, "Document"),
      "description": displayText(row.description ?? row.body ?? row.excerpt, ""),
      "url": absoluteUrl(url, href),
      "encodingFormat": displayText(row.mime_type ?? row.mimeType ?? row.file_type ?? row.fileType, ""),
      "dateCreated": displayText(row.created_at ?? row.createdAt, "")
    };
  }
  if (view.module === "search" && isHashtagRow(row)) {
    const href = hashtagRowHref(row, pageAbsoluteUrl(url));
    return {
      "@type": "Thing",
      "@id": `${absoluteUrl(url, href)}#hashtag`,
      "name": hashtagRowLabel(row, "Hashtag"),
      "url": absoluteUrl(url, href),
      "description": displayText(row.description ?? row.body ?? row.excerpt, "")
    };
  }
  if (view.module === "saved") {
    const href = savedRowHref(row, pageAbsoluteUrl(url));
    return {
      "@type": "CreativeWork",
      "@id": `${absoluteUrl(url, href)}#saved`,
      "name": savedRowTitle(row, "Saved item"),
      "description": displayText(row.excerpt ?? row.description ?? row.body ?? row.subject_description ?? row.subjectDescription, ""),
      "url": absoluteUrl(url, href),
      "dateCreated": displayText(row.created_at ?? row.createdAt, "")
    };
  }
  if (view.module === "articles" || row.article_slug || row.articleSlug) {
    const href = articleRowHref(row, pageAbsoluteUrl(url));
    return {
      "@type": "Article",
      "@id": `${absoluteUrl(url, href)}#article`,
      "headline": displayText(row.title ?? row.name, "Article"),
      "description": displayText(row.excerpt ?? row.description ?? row.body, ""),
      "url": absoluteUrl(url, href),
      "image": mediaRowUrl(url, row),
      "datePublished": displayText(row.published_at ?? row.created_at ?? row.createdAt, ""),
      "dateModified": displayText(row.updated_at ?? row.updatedAt, ""),
      "author": articleAuthorStructuredData(url, row)
    };
  }
  if (view.module === "clips" || row.video_url || row.videoUrl) {
    const contentUrl = mediaRowUrl(url, { ...row, media_url: row.video_url ?? row.videoUrl ?? row.media_url ?? row.mediaUrl });
    return {
      "@type": "VideoObject",
      "@id": `${contentUrl ?? pageAbsoluteUrl(url)}#video`,
      "name": displayText(row.title ?? row.caption ?? row.body, "Video"),
      "description": displayText(row.caption ?? row.body ?? row.description, ""),
      "contentUrl": contentUrl,
      "thumbnailUrl": absoluteOptionalUrl(url, row.thumbnail_url ?? row.thumbnailUrl ?? row.image_url ?? row.imageUrl),
      "uploadDate": displayText(row.published_at ?? row.created_at ?? row.createdAt, "")
    };
  }
  if (view.module === "photos" || row.media_url || row.mediaUrl || row.image_url || row.imageUrl || row.photo_url || row.photoUrl) {
    const contentUrl = mediaRowUrl(url, row);
    const href = mediaPostHref(row);
    return {
      "@type": "ImageObject",
      "@id": `${contentUrl ?? pageAbsoluteUrl(url)}#image`,
      "name": displayText(row.title ?? row.caption ?? row.body, "Photo"),
      "caption": displayText(row.caption ?? row.body ?? row.description, ""),
      "url": href ? absoluteUrl(url, href) : contentUrl,
      "contentUrl": contentUrl,
      "datePublished": displayText(row.published_at ?? row.created_at ?? row.createdAt, ""),
      "dateModified": displayText(row.updated_at ?? row.updatedAt, ""),
      "author": imageAuthorStructuredData(url, row, profile)
    };
  }
  if (!isPostLikeRow(row) && (view.module === "network" || row.username)) {
    const href = publicProfilePath(row);
    return {
      "@type": "Person",
      "@id": `${absoluteUrl(url, href)}#person`,
      "name": personDisplayName(row, "Member"),
      "url": absoluteUrl(url, href),
      "image": profileImageUrl(url, row)
    };
  }
  const postHref = canonicalAppHref(postRowHref(row) || row.href || row.url);
  const postImages = postImageUrls(url, row);
  return {
    "@type": "SocialMediaPosting",
    "@id": `${absoluteUrl(url, postHref || pageAbsoluteUrl(url))}#post`,
    "text": displayText(row.body ?? row.title ?? row.description, ""),
    "url": postHref ? absoluteUrl(url, postHref) : pageAbsoluteUrl(url),
    "datePublished": displayText(row.created_at ?? row.createdAt ?? row.published_at ?? row.publishedAt, ""),
    "dateModified": displayText(row.updated_at ?? row.updatedAt, ""),
    "image": postImages.length ? postImages : undefined,
    "author": profile ? { "@id": `${absoluteUrl(url, publicProfilePath(profile))}#person` } : postAuthorStructuredData(url, row, siteName)
  };
}

function crawlableRowHtml(url: URL, view: ViewCatalogItem, row: Record<string, unknown>): string {
  if (view.module === "saved") {
    const href = savedRowHref(row, "");
    const title = savedRowTitle(row, "Saved item");
    const body = displayText(row.excerpt ?? row.description ?? row.body ?? row.subject_description ?? row.subjectDescription ?? row.collection ?? row.subject_type ?? row.subjectType, "");
    const createdAt = displayText(row.created_at ?? row.createdAt, "");
    return `<article><h3>${href ? `<a href="${escapeHtml(href)}">${escapeHtml(title)}</a>` : escapeHtml(title)}</h3>${body ? `<p>${escapeHtml(body)}</p>` : ""}${createdAt ? `<time datetime="${escapeHtml(createdAt)}">${escapeHtml(createdAt)}</time>` : ""}</article>`;
  }
  if (view.module === "events" || view.module === "groups" || view.module === "pages") {
    const href = directoryRowHref(row, view.module, "");
    const title = displayText(row.title ?? row.name ?? row.slug, view.module === "events" ? "Event" : view.module === "groups" ? "Group" : "Page");
    const body = displayText(row.description ?? row.body ?? row.excerpt, "");
    const createdAt = displayText(row.starts_at ?? row.startsAt ?? row.created_at ?? row.createdAt ?? row.published_at ?? row.publishedAt, "");
    const imageUrl = mediaRowUrl(url, row);
    return `<article>${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(title)}">` : ""}<h3>${href ? `<a href="${escapeHtml(href)}">${escapeHtml(title)}</a>` : escapeHtml(title)}</h3>${body ? `<p>${escapeHtml(body)}</p>` : ""}${createdAt ? `<time datetime="${escapeHtml(createdAt)}">${escapeHtml(createdAt)}</time>` : ""}</article>`;
  }
  if (view.module === "documents") {
    const href = documentRowHref(row, "");
    const title = documentRowTitle(row, "Document");
    const body = displayText(row.description ?? row.body ?? row.excerpt ?? row.document_type ?? row.documentType, "");
    const createdAt = displayText(row.created_at ?? row.createdAt ?? row.updated_at ?? row.updatedAt, "");
    return `<article><h3>${href ? `<a href="${escapeHtml(href)}">${escapeHtml(title)}</a>` : escapeHtml(title)}</h3>${body ? `<p>${escapeHtml(body)}</p>` : ""}${createdAt ? `<time datetime="${escapeHtml(createdAt)}">${escapeHtml(createdAt)}</time>` : ""}</article>`;
  }
  if (view.module === "search" && isHashtagRow(row)) {
    const href = hashtagRowHref(row, "");
    const title = hashtagRowLabel(row, "Hashtag");
    const body = displayText(row.description ?? row.body ?? row.excerpt, "");
    return `<article><h3>${href ? `<a href="${escapeHtml(href)}">${escapeHtml(title)}</a>` : escapeHtml(title)}</h3>${body ? `<p>${escapeHtml(body)}</p>` : ""}</article>`;
  }
  if (view.module === "articles" || row.article_slug || row.articleSlug) {
    const href = articleRowHref(row, "");
    const title = displayText(row.title ?? row.name, "Article");
    const body = displayText(row.excerpt ?? row.description ?? row.body, "");
    const authorName = authorDisplayName(row, "");
    const authorPath = authorProfilePath(row);
    const createdAt = displayText(row.created_at ?? row.createdAt ?? row.published_at ?? row.publishedAt, "");
    const imageUrl = mediaRowUrl(url, row);
    const authorHtml = authorName ? `<p>${authorPath ? `<a href="${escapeHtml(authorPath)}" rel="author">${escapeHtml(authorName)}</a>` : escapeHtml(authorName)}</p>` : "";
    return `<article>${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(title)}">` : ""}<h3>${href ? `<a href="${escapeHtml(href)}">${escapeHtml(title)}</a>` : escapeHtml(title)}</h3>${authorHtml}${body ? `<p>${escapeHtml(body)}</p>` : ""}${createdAt ? `<time datetime="${escapeHtml(createdAt)}">${escapeHtml(createdAt)}</time>` : ""}</article>`;
  }
  if (view.module === "clips" || row.video_url || row.videoUrl) {
    const mediaUrl = mediaRowUrl(url, { ...row, media_url: row.video_url ?? row.videoUrl ?? row.media_url ?? row.mediaUrl });
    const title = displayText(row.title ?? row.caption ?? row.body, "Video");
    const body = displayText(row.caption ?? row.body ?? row.description, "");
    const createdAt = displayText(row.created_at ?? row.createdAt ?? row.published_at ?? row.publishedAt, "");
    return `<article>${mediaUrl ? `<video src="${escapeHtml(mediaUrl)}" controls preload="metadata"></video>` : ""}<h3>${escapeHtml(title)}</h3>${body ? `<p>${escapeHtml(body)}</p>` : ""}${createdAt ? `<time datetime="${escapeHtml(createdAt)}">${escapeHtml(createdAt)}</time>` : ""}</article>`;
  }
  if (view.module === "photos" || row.media_url || row.mediaUrl || row.image_url || row.imageUrl || row.photo_url || row.photoUrl) {
    const mediaUrl = mediaRowUrl(url, row);
    const href = mediaPostHref(row);
    const title = displayText(row.title ?? row.caption ?? row.body, "Photo");
    const body = displayText(row.caption ?? row.body ?? row.description, "");
    return `<article>${mediaUrl ? `<img src="${escapeHtml(mediaUrl)}" alt="${escapeHtml(title)}">` : ""}<h3>${href ? `<a href="${escapeHtml(href)}">${escapeHtml(title)}</a>` : escapeHtml(title)}</h3>${body ? `<p>${escapeHtml(body)}</p>` : ""}</article>`;
  }
  if (!isPostLikeRow(row) && (view.module === "network" || row.username)) {
    const name = personDisplayName(row, "Member");
    const imageUrl = crawlableImageUrl(url, row);
    return `<article>${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(name)}">` : ""}<h3><a href="${escapeHtml(publicProfilePath(row))}">${escapeHtml(name)}</a></h3>${row.bio || row.description || row.headline ? `<p>${escapeHtml(displayText(row.bio ?? row.description ?? row.headline, ""))}</p>` : ""}</article>`;
  }
  const href = canonicalAppHref(postRowHref(row) || row.href || row.url);
  const title = displayText(row.title ?? row.name ?? row.display_name ?? row.displayName ?? row.author_display_name ?? row.authorDisplayName ?? row.username, "");
  const authorName = authorDisplayName(row, "");
  const authorPath = authorProfilePath(row);
  const body = displayText(row.body ?? row.excerpt ?? row.caption ?? row.description, "");
  const createdAt = displayText(row.created_at ?? row.createdAt ?? row.published_at ?? row.publishedAt ?? row.starts_at ?? row.startsAt, "");
  const mediaHtml = postImageUrls(url, row).map((mediaUrl, index) => `<img src="${escapeHtml(mediaUrl)}" alt="${escapeHtml(title || `Image ${index + 1}`)}">`).join("");
  const authorHtml = authorName ? `<p>${authorPath ? `<a href="${escapeHtml(authorPath)}" rel="author">${escapeHtml(authorName)}</a>` : escapeHtml(authorName)}</p>` : "";
  return `<article${row.id ? ` id="${escapeHtml(String(row.id))}"` : ""}>${authorHtml}${title ? `<h3>${href ? `<a href="${escapeHtml(href)}">${escapeHtml(title)}</a>` : escapeHtml(title)}</h3>` : ""}${mediaHtml}${body ? `<p>${escapeHtml(body)}</p>` : ""}${createdAt ? `<time datetime="${escapeHtml(createdAt)}">${escapeHtml(createdAt)}</time>` : ""}</article>`;
}

function crawlableImageUrl(url: URL, row: Record<string, unknown>): string | undefined {
  const objectKey = displayText(row.media_object_key ?? row.mediaObjectKey ?? row.image_object_key ?? row.imageObjectKey ?? row.photo_object_key ?? row.photoObjectKey ?? row.thumbnail_object_key ?? row.thumbnailObjectKey, "");
  if (objectKey) return mediaObjectUrl(url, objectKey);
  return absoluteOptionalUrl(url, row.media_url ?? row.mediaUrl ?? row.image_url ?? row.imageUrl ?? row.photo_url ?? row.photoUrl ?? row.public_url ?? row.publicUrl ?? row.thumbnail_url ?? row.thumbnailUrl ?? row.avatar_url ?? row.avatarUrl ?? row.profile_avatar_url ?? row.profileAvatarUrl);
}

function postImageUrls(url: URL, row: Record<string, unknown>): string[] {
  const itemUrls = [
    row.media_items_json,
    row.post_media_json,
    row.items_json,
    row.media_json,
    row.items,
    row.media
  ].flatMap(arrayFromJson)
    .filter(isRecord)
    .map(normalizePostMediaItem)
    .filter(isImageMediaItem)
    .map((item) => mediaRowUrl(url, item))
    .filter((item): item is string => Boolean(item));
  const directUrl = contentImageUrl(url, row);
  return uniqueStrings(directUrl ? [...itemUrls, directUrl] : itemUrls);
}

function contentImageUrl(url: URL, row: Record<string, unknown>): string | undefined {
  const objectKey = displayText(row.media_object_key ?? row.mediaObjectKey ?? row.image_object_key ?? row.imageObjectKey ?? row.photo_object_key ?? row.photoObjectKey ?? row.thumbnail_object_key ?? row.thumbnailObjectKey, "");
  if (objectKey) return mediaObjectUrl(url, objectKey);
  return absoluteOptionalUrl(url, row.media_url ?? row.mediaUrl ?? row.image_url ?? row.imageUrl ?? row.photo_url ?? row.photoUrl ?? row.public_url ?? row.publicUrl ?? row.thumbnail_url ?? row.thumbnailUrl);
}

function normalizePostMediaItem(item: Record<string, unknown>): Record<string, unknown> {
  const metadata = recordFromJson(item.metadata ?? item.metadata_json);
  return {
    ...item,
    object_key: item.object_key ?? item.objectKey ?? item.bucket_key ?? item.bucketKey ?? item.file_key ?? item.fileKey ?? metadata.object_key ?? metadata.objectKey,
    public_url: item.public_url ?? item.publicUrl ?? item.url ?? item.src ?? metadata.public_url ?? metadata.publicUrl ?? metadata.url,
    content_type: item.content_type ?? item.contentType ?? item.mime_type ?? item.mimeType ?? metadata.content_type ?? metadata.contentType ?? metadata.mime,
    type: item.type ?? metadata.type
  };
}

function isImageMediaItem(item: Record<string, unknown>): boolean {
  const itemType = displayText(item.type, "").toLowerCase();
  const contentType = displayText(item.content_type ?? item.contentType, "").toLowerCase();
  if (itemType === "video" || itemType === "movie" || itemType === "clip") return false;
  return !contentType || contentType.startsWith("image/");
}

function arrayFromJson(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function recordFromJson(value: unknown): Record<string, unknown> {
  if (isRecord(value)) return value;
  if (typeof value !== "string" || !value.trim()) return {};
  try {
    const parsed = JSON.parse(value) as unknown;
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function profileTabEndpointFor(tab: string, identifier: string): string | null {
  const encoded = encodeURIComponent(identifier);
  if (tab === "friends") return `/api/profile/friends?username=${encoded}`;
  if (tab === "photos") return `/api/profile/media?username=${encoded}`;
  if (tab === "articles") return `/api/profile/articles?username=${encoded}`;
  if (tab === "timeline") return `/api/profile/posts?username=${encoded}`;
  return null;
}

function profileEndpointForIdentifier(identifier: string): string {
  return `/api/profile${identifier ? `?username=${encodeURIComponent(identifier)}` : ""}`;
}

function profileTabFromPath(pathname: string): string {
  const parts = pathname.split("/");
  const segment = pathname.startsWith("/profile/") ? parts[3] ?? "" : parts[2] ?? "";
  return ["about", "friends", "photos", "articles"].includes(segment) ? segment : "timeline";
}

function profileIdentifierFromPath(pathname: string): string {
  const segment = pathname.startsWith("/@") ? pathname.split("/")[1] : pathname.startsWith("/profile/") ? pathname.split("/")[2] : "";
  return decodeProfileHandle(segment ?? "");
}

function decodeProfileHandle(segment: string): string {
  const clean = segment.startsWith("@") ? segment.slice(1) : segment;
  try {
    return decodeURIComponent(clean);
  } catch {
    return clean;
  }
}

function feedEndpointFromQuery(currentQuery: Record<string, string>): string {
  const scope = postFeedScopeFromQuery(currentQuery);
  return scope ? `/api/posts?scope=${encodeURIComponent(scope)}` : "/api/posts";
}

function postFeedScopeFromQuery(currentQuery: Record<string, string>): string {
  const scope = displayText(currentQuery.scope, "").toLowerCase();
  return ["friends", "groups", "pages"].includes(scope) ? scope : "";
}

function postDetailEndpoint(path: string): string {
  const identifier = path.startsWith("/post/") ? decodeProfileHandle(path.split("/")[2] ?? "").trim() : "";
  return identifier ? `/api/posts/${encodeURIComponent(identifier)}` : "/api/posts";
}

function hashtagQueryFromPath(path: string): string {
  if (!path.startsWith("/hashtags/")) return "";
  const tag = decodeProfileHandle(path.split("/")[2] ?? "").replace(/^#+/u, "").trim();
  return tag ? `#${tag}` : "";
}

function directoryEndpoint(basePath: string, filter: string, currentQuery: Record<string, string>): string {
  const params = new URLSearchParams();
  const query = filter.trim() || displayText(currentQuery.q, "");
  const category = displayText(currentQuery.category, "");
  if (query) params.set("q", query);
  if (category) params.set("category", category);
  const suffix = params.toString();
  return `${basePath}${suffix ? `?${suffix}` : ""}`;
}

function directoryEntityEndpoint(routeBasePath: string, apiBasePath: string, path: string): string | null {
  if (path === routeBasePath || !path.startsWith(`${routeBasePath}/`)) return null;
  const segment = path.slice(routeBasePath.length + 1).split("/")[0] ?? "";
  const identifier = decodeProfileHandle(segment).trim();
  if (!identifier || ["admin", "create", "editor", "feature", "managed", "manage_users", "notify_settings", "reports"].includes(identifier)) return null;
  return `${apiBasePath}/${encodeURIComponent(identifier)}`;
}

function normalizeAppPath(value: string): string {
  const path = value.split("?")[0].split("#")[0].replace(/\/+$/u, "");
  return path || "/";
}

function queryRecordFromUrl(url: URL): Record<string, string> {
  return Object.fromEntries(url.searchParams.entries());
}

function mergedQueryRecord(url: URL, currentQuery: Record<string, string> | undefined): Record<string, string> {
  return { ...queryRecordFromUrl(url), ...(currentQuery ?? {}) };
}

export function openSNInitialDataPayload(initialData: Record<string, unknown>, endpoint: string): unknown {
  if (Object.prototype.hasOwnProperty.call(initialData, endpoint)) return initialData[endpoint];
  const aliases = profileEndpointAliases(initialData, endpoint);
  for (const alias of aliases) {
    if (Object.prototype.hasOwnProperty.call(initialData, alias)) return initialData[alias];
  }
  const compatibleTargets = [endpoint, ...aliases];
  const match = Object.entries(initialData).find(([candidate]) => compatibleTargets.some((target) => endpointsCompatible(candidate, target)));
  return match ? match[1] : undefined;
}

function profileEndpointAliases(initialData: Record<string, unknown>, endpoint: string): string[] {
  const targetUrl = endpointUrl(endpoint);
  if (!targetUrl || !isProfileEndpointPath(targetUrl.pathname) || !targetUrl.searchParams.has("username")) return [];
  const profile = firstProfileRow(initialData);
  if (!profile) return [];
  return profileAliasIdentifiers(profile).map((identifier) => {
    const aliasUrl = new URL(targetUrl.toString());
    aliasUrl.searchParams.set("username", identifier);
    return `${aliasUrl.pathname}${aliasUrl.search}`;
  }).filter((alias) => alias !== endpoint);
}

function profileAliasIdentifiers(profile: Record<string, unknown>): string[] {
  return uniqueStrings([
    publicProfileIdentifier(profile),
    displayText(profile.username, ""),
    displayText(profile.profile_slug, ""),
    displayText(profile.profileSlug, ""),
    displayText(profile.public_id, ""),
    displayText(profile.publicId, "")
  ]);
}

function endpointsCompatible(candidate: string, target: string): boolean {
  const candidateUrl = endpointUrl(candidate);
  const targetUrl = endpointUrl(target);
  if (!candidateUrl || !targetUrl || candidateUrl.pathname !== targetUrl.pathname) return false;
  if (!Array.from(targetUrl.searchParams.entries()).every(([key, value]) => candidateUrl.searchParams.get(key) === value)) return false;
  return Array.from(candidateUrl.searchParams.entries()).every(([key, value]) => targetUrl.searchParams.has(key) || isInitialDataPaginationParam(key, value));
}

function endpointUrl(value: string): URL | null {
  try {
    return new URL(value, "https://opensn.local");
  } catch {
    return null;
  }
}

function isInitialDataPageSizeParam(key: string): boolean {
  return ["limit", "page_size", "pageSize", "per_page", "perPage", "take"].includes(key);
}

function isInitialDataPaginationParam(key: string, value: string): boolean {
  if (isInitialDataPageSizeParam(key)) return true;
  if (["page", "p"].includes(key)) return value === "1";
  if (["offset", "skip", "start"].includes(key)) return value === "0";
  if (["cursor", "next_cursor", "nextCursor"].includes(key)) return value === "";
  return false;
}

function payloadRows(payload: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(payload)) return payload.filter(isRecord);
  if (!isRecord(payload)) return [];
  if (Array.isArray(payload.data)) return payload.data.filter(isRecord);
  if (isRecord(payload.data)) return recordPayloadRows(payload.data) ?? singlePayloadRow(payload.data) ?? [payload.data];
  const rows = recordPayloadRows(payload);
  if (rows) return rows;
  const singleRow = singlePayloadRow(payload);
  if (singleRow) return singleRow;
  if ("ok" in payload || "id" in payload || "user" in payload) return [payload];
  return [];
}

function recordPayloadRows(payload: Record<string, unknown>): Array<Record<string, unknown>> | undefined {
  const keys = ["rows", "items", "results", "records", "posts", "friends", "follows", "users", "members", "profiles", "media", "photos", "articles", "events", "groups", "pages", "documents", "clips", "videos", "notifications", "bookmarks", "invites", "likes", "reactions", "shares", "hashtags", "tags", "categories", "messages", "rooms", "conversations", "tickets"];
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key].filter(isRecord);
  }
  const entries = Object.entries(payload).filter(([, value]) => Array.isArray(value));
  return entries.length ? entries.flatMap(([, value]) => (value as unknown[]).filter(isRecord)) : undefined;
}

function singlePayloadRow(payload: Record<string, unknown>): Array<Record<string, unknown>> | undefined {
  const keys = ["post", "article", "event", "group", "page", "document", "profile", "user", "member", "item", "follow", "like", "reaction", "share", "hashtag", "tag", "category", "message", "room", "conversation", "ticket"];
  for (const key of keys) {
    if (isRecord(payload[key])) return [payload[key]];
  }
  return undefined;
}

function payloadItemCount(payload: unknown, fallback: number): number {
  if (!isRecord(payload)) return fallback;
  const data = isRecord(payload.data) ? payload.data : null;
  const meta = isRecord(payload.meta) ? payload.meta : data && isRecord(data.meta) ? data.meta : null;
  return numberCandidate(payload.total ?? payload.total_count ?? payload.totalCount ?? payload.count ?? data?.total ?? data?.total_count ?? data?.totalCount ?? data?.count ?? meta?.total ?? meta?.total_count ?? meta?.totalCount ?? meta?.count) ?? fallback;
}

function numberCandidate(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return Math.max(0, Math.floor(value));
  const text = String(value ?? "").trim();
  if (!/^\d+$/u.test(text)) return undefined;
  return Number(text);
}

function isPostLikeRow(row: Record<string, unknown>): boolean {
  return Boolean(row.body || row.author_id || row.authorId || row.post_id || row.postId || row.published_at || row.publishedAt);
}

function publicProfilePath(row: Record<string, unknown>): string {
  const username = publicProfileIdentifier(row);
  return username ? `/profile/${encodeURIComponent(username)}` : "/network";
}

function authorProfilePath(row: Record<string, unknown>): string {
  const username = authorProfileIdentifier(row);
  return username ? `/profile/${encodeURIComponent(username)}` : "";
}

function personDisplayName(row: Record<string, unknown>, fallback: string): string {
  return displayText(row.display_name ?? row.displayName ?? row.name ?? row.username, fallback);
}

function authorDisplayName(row: Record<string, unknown>, fallback: string): string {
  return displayText(row.author_display_name ?? row.authorDisplayName ?? row.author_name ?? row.authorName ?? row.owner_display_name ?? row.ownerDisplayName ?? row.user_display_name ?? row.userDisplayName ?? row.display_name ?? row.displayName ?? row.author_username ?? row.authorUsername ?? row.username, fallback);
}

function publicProfileIdentifier(row: Record<string, unknown>): string {
  return firstProfileIdentifier(row.profile_slug, row.profileSlug, row.public_id, row.publicId, row.username);
}

function authorProfileIdentifier(row: Record<string, unknown>): string {
  return firstProfileIdentifier(row.author_profile_slug, row.authorProfileSlug, row.author_public_id, row.authorPublicId, row.profile_slug, row.profileSlug, row.public_id, row.publicId, row.author_username, row.authorUsername, row.username);
}

function normalizedProfileIdentifier(value: unknown): string {
  const identifier = displayText(value, "");
  if (identifier.startsWith("/@")) return identifier.slice(2);
  return identifier.startsWith("@") ? identifier.slice(1) : identifier;
}

function firstProfileIdentifier(...values: unknown[]): string {
  for (const value of values) {
    const identifier = normalizedProfileIdentifier(value);
    if (identifier) return identifier;
  }
  return "";
}

function postAuthorStructuredData(url: URL, row: Record<string, unknown>, fallback: string): Record<string, unknown> {
  const href = authorProfilePath(row);
  return {
    "@type": "Person",
    "@id": href ? `${absoluteUrl(url, href)}#person` : undefined,
    "name": authorDisplayName(row, fallback),
    "url": href ? absoluteUrl(url, href) : undefined,
    "image": authorImageUrl(url, row)
  };
}

function articleAuthorStructuredData(url: URL, row: Record<string, unknown>): Record<string, unknown> | undefined {
  const href = authorProfilePath(row);
  const name = authorDisplayName(row, "");
  if (!href && !name) return undefined;
  return {
    "@type": "Person",
    "@id": href ? `${absoluteUrl(url, href)}#person` : undefined,
    "name": name || undefined,
    "url": href ? absoluteUrl(url, href) : undefined,
    "image": authorImageUrl(url, row)
  };
}

function imageAuthorStructuredData(url: URL, row: Record<string, unknown>, profile: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (profile) return { "@id": `${absoluteUrl(url, publicProfilePath(profile))}#person` };
  return articleAuthorStructuredData(url, row);
}

function articleRowHref(row: Record<string, unknown>, fallback: string): string {
  const explicitHref = displayText(row.href ?? row.url ?? row.article_url ?? row.articleUrl, "");
  if (explicitHref) return canonicalAppHref(explicitHref);
  const slug = displayText(row.slug ?? row.article_slug ?? row.articleSlug ?? row.id, "");
  return slug ? `/articles/${encodeURIComponent(slug)}` : fallback;
}

function savedRowTitle(row: Record<string, unknown>, fallback: string): string {
  return displayText(row.title ?? row.subject_title ?? row.subjectTitle ?? row.post_title ?? row.postTitle ?? row.name ?? row.body, fallback);
}

function postRowHref(row: Record<string, unknown>): string {
  const identifier = displayText(row.slug ?? row.post_slug ?? row.postSlug ?? row.id ?? row.post_id ?? row.postId, "");
  return identifier ? `/post/${encodeURIComponent(identifier)}` : "";
}

function savedRowHref(row: Record<string, unknown>, fallback: string): string {
  const explicitHref = displayText(row.href ?? row.url ?? row.subject_href ?? row.subjectHref ?? row.permalink_url ?? row.permalinkUrl, "");
  if (explicitHref) return canonicalAppHref(explicitHref);
  const subjectType = displayText(row.subject_type ?? row.subjectType, "").toLowerCase();
  const subjectIdentifier = displayText(row.post_slug ?? row.postSlug ?? row.subject_slug ?? row.subjectSlug ?? row.slug ?? row.subject_id ?? row.subjectId, "");
  if (!subjectIdentifier) return fallback;
  if (subjectType === "post") return `/post/${encodeURIComponent(subjectIdentifier)}`;
  if (subjectType === "article") return `/articles/${encodeURIComponent(subjectIdentifier)}`;
  if (subjectType === "event") return `/events/${encodeURIComponent(subjectIdentifier)}`;
  if (subjectType === "group") return `/groups/${encodeURIComponent(subjectIdentifier)}`;
  if (subjectType === "page") return `/pages/${encodeURIComponent(subjectIdentifier)}`;
  return fallback;
}

function mediaPostHref(row: Record<string, unknown>): string {
  const explicitHref = displayText(row.href ?? row.permalink_url ?? row.permalinkUrl ?? row.post_href ?? row.postHref ?? row.post_url ?? row.postUrl, "");
  if (explicitHref && !isMediaAssetHref(explicitHref)) return canonicalAppHref(explicitHref);
  const postIdentifier = displayText(row.post_slug ?? row.postSlug ?? row.post_id ?? row.postId ?? row.source_post_slug ?? row.sourcePostSlug ?? row.source_post_id ?? row.sourcePostId, "");
  return postIdentifier ? `/post/${encodeURIComponent(postIdentifier)}` : "";
}

function isMediaAssetHref(value: string): boolean {
  const path = value.split("?")[0].split("#")[0].toLowerCase();
  return path.startsWith("/media/") || /\.(avif|gif|jpe?g|png|webp)$/u.test(path);
}

function mediaRowUrl(url: URL, row: Record<string, unknown>): string | undefined {
  const objectKey = displayText(row.media_object_key ?? row.mediaObjectKey ?? row.cover_media_object_key ?? row.coverMediaObjectKey ?? row.cover_object_key ?? row.coverObjectKey ?? row.object_key ?? row.objectKey ?? row.bucket_key ?? row.bucketKey ?? row.file_key ?? row.fileKey, "");
  if (objectKey) return mediaObjectUrl(url, objectKey);
  return absoluteOptionalUrl(url, row.media_url ?? row.mediaUrl ?? row.cover_image_url ?? row.coverImageUrl ?? row.cover_url ?? row.coverUrl ?? row.image_url ?? row.imageUrl ?? row.photo_url ?? row.photoUrl ?? row.public_url ?? row.publicUrl ?? row.url ?? row.src);
}

function profileImageUrl(url: URL, row: Record<string, unknown>): string | undefined {
  const objectKey = displayText(row.avatar_object_key ?? row.avatarObjectKey ?? row.profile_avatar_object_key ?? row.profileAvatarObjectKey, "");
  if (objectKey) return mediaObjectUrl(url, objectKey);
  return absoluteOptionalUrl(url, row.avatar_url ?? row.avatarUrl ?? row.profile_avatar_url ?? row.profileAvatarUrl);
}

function authorImageUrl(url: URL, row: Record<string, unknown>): string | undefined {
  const objectKey = displayText(row.author_avatar_object_key ?? row.authorAvatarObjectKey ?? row.avatar_object_key ?? row.avatarObjectKey ?? row.profile_avatar_object_key ?? row.profileAvatarObjectKey, "");
  if (objectKey) return mediaObjectUrl(url, objectKey);
  return absoluteOptionalUrl(url, row.author_avatar_url ?? row.authorAvatarUrl ?? row.avatar_url ?? row.avatarUrl ?? row.profile_avatar_url ?? row.profileAvatarUrl);
}

function mediaObjectUrl(url: URL, objectKey: string): string {
  return absoluteUrl(url, `/media/${objectKey.split("/").map((part) => encodeURIComponent(part)).join("/")}`);
}

function directoryRowHref(row: Record<string, unknown>, module: string, fallback: string): string {
  const id = displayText(row.id ?? row[`${module.slice(0, -1)}_id`] ?? row[`${module.slice(0, -1)}Id`], "");
  const slug = displayText(row.slug ?? row[`${module.slice(0, -1)}_slug`] ?? row[`${module.slice(0, -1)}Slug`] ?? row.normalized_tag ?? row.username ?? id, "");
  if (id || slug) return `/${module}/${encodeURIComponent(slug || id)}`;
  const explicitHref = displayText(row.href ?? row.url ?? row[`${module.slice(0, -1)}_url`] ?? row[`${module.slice(0, -1)}Url`], "");
  return explicitHref ? canonicalAppHref(explicitHref) : fallback;
}

function eventAttendanceMode(row: Record<string, unknown>, onlineUrl: string): string {
  const format = displayText(row.format ?? row.event_format ?? row.eventFormat ?? row.type, "").toLowerCase();
  const hasOnline = Boolean(onlineUrl || format.includes("online") || format.includes("virtual"));
  const hasPlace = Boolean(eventPhysicalLocation(row));
  if (hasOnline && hasPlace) return "https://schema.org/MixedEventAttendanceMode";
  return hasOnline ? "https://schema.org/OnlineEventAttendanceMode" : "https://schema.org/OfflineEventAttendanceMode";
}

function eventStatusUrl(row: Record<string, unknown>): string {
  const status = displayText(row.status ?? row.event_status ?? row.eventStatus, "").toLowerCase();
  if (status === "cancelled" || status === "canceled") return "https://schema.org/EventCancelled";
  if (status === "postponed") return "https://schema.org/EventPostponed";
  if (status === "rescheduled") return "https://schema.org/EventRescheduled";
  if (status === "moved_online" || status === "moved-online") return "https://schema.org/EventMovedOnline";
  return "https://schema.org/EventScheduled";
}

function eventStructuredLocation(url: URL, row: Record<string, unknown>, onlineUrl: string): Record<string, unknown> | Array<Record<string, unknown>> | undefined {
  const location = eventPhysicalLocation(row);
  const place = location ? { "@type": "Place", "name": location, "address": location } : undefined;
  const virtualLocation = onlineUrl ? { "@type": "VirtualLocation", "url": absoluteUrl(url, onlineUrl) } : undefined;
  if (place && virtualLocation) return [place, virtualLocation];
  return virtualLocation ?? place;
}

function eventPhysicalLocation(row: Record<string, unknown>): string {
  return displayText(row.location ?? row.location_name ?? row.locationName ?? row.venue_name ?? row.venueName ?? row.venue ?? row.address, "");
}

function eventOrganizers(url: URL, row: Record<string, unknown>): Array<Record<string, unknown>> | undefined {
  const hostRows = [row.hosts_json, row.hostsJson, row.hosts].flatMap(arrayFromJson).filter(isRecord);
  const organizers = hostRows.map((host) => eventOrganizer(url, host)).filter((item): item is Record<string, unknown> => Boolean(item));
  const directHostName = displayText(row.host_name ?? row.hostName ?? row.organizer_name ?? row.organizerName ?? row.owner_display_name ?? row.ownerDisplayName, "");
  if (directHostName) organizers.push({ "@type": "Organization", "name": directHostName });
  const uniqueOrganizers = uniqueByJsonLdIdOrName(organizers);
  return uniqueOrganizers.length ? uniqueOrganizers : undefined;
}

function eventOrganizer(url: URL, row: Record<string, unknown>): Record<string, unknown> | undefined {
  const name = displayText(row.display_name ?? row.displayName ?? row.name ?? row.username, "");
  if (!name) return undefined;
  const username = publicProfileIdentifier(row);
  return {
    "@type": username ? "Person" : "Organization",
    "@id": username ? `${absoluteUrl(url, publicProfilePath(row))}#person` : undefined,
    "name": name,
    "url": username ? absoluteUrl(url, publicProfilePath(row)) : absoluteOptionalUrl(url, row.url ?? row.href)
  };
}

function uniqueByJsonLdIdOrName(rows: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = displayText(row["@id"] ?? row.name, "");
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function documentRowTitle(row: Record<string, unknown>, fallback: string): string {
  return displayText(row.title ?? row.name ?? row.filename ?? row.file_name ?? row.fileName, fallback);
}

function documentRowHref(row: Record<string, unknown>, fallback: string): string {
  const explicitHref = displayText(row.href ?? row.url ?? row.document_url ?? row.documentUrl ?? row.file_url ?? row.fileUrl, "");
  if (explicitHref) return canonicalAppHref(explicitHref);
  const identifier = displayText(row.slug ?? row.id ?? row.document_id ?? row.documentId ?? row.file_id ?? row.fileId, "");
  return identifier ? `/documents/${encodeURIComponent(identifier)}` : fallback;
}

function isHashtagRow(row: Record<string, unknown>): boolean {
  return Boolean(row.normalized_tag || String(row.tag ?? "").trim().startsWith("#"));
}

function hashtagRowLabel(row: Record<string, unknown>, fallback: string): string {
  const tag = displayText(row.normalized_tag ?? row.tag ?? row.title ?? row.name, "");
  return tag ? `#${tag.replace(/^#+/u, "")}` : fallback;
}

function hashtagRowHref(row: Record<string, unknown>, fallback: string): string {
  const tag = displayText(row.normalized_tag ?? row.tag, "").replace(/^#+/u, "");
  return tag ? `/hashtags/${encodeURIComponent(tag)}` : fallback;
}

function pageAbsoluteUrl(url: URL): string {
  const pageUrl = new URL(url.toString());
  pageUrl.pathname = canonicalOpenSNPath(pageUrl.pathname);
  removeTransientPageParams(pageUrl.searchParams);
  const search = pageUrl.searchParams.toString();
  return `${pageUrl.origin}${pageUrl.pathname}${search ? `?${search}` : ""}`;
}

function removeTransientPageParams(params: URLSearchParams): void {
  Array.from(params.entries()).forEach(([key, value]) => {
    if (isTransientPageParam(key, value)) params.delete(key);
  });
}

function isTransientPageParam(key: string, value: string): boolean {
  if (["cursor", "next_cursor", "nextCursor"].includes(key)) return true;
  return isInitialDataPaginationParam(key, value);
}

function canonicalAppHref(value: unknown): string {
  const href = displayText(value, "");
  if (!href) return "";
  const absolute = /^[a-z][a-z0-9+.-]*:\/\//iu.test(href);
  if (absolute) {
    try {
      const url = new URL(href);
      const path = canonicalOpenSNPath(url.pathname);
      return path === url.pathname ? href : `${url.origin}${path}${url.search}${url.hash}`;
    } catch {
      return href;
    }
  }
  const match = href.match(/^([^?#]*)([?#].*)?$/u);
  const path = match?.[1] ?? href;
  const suffix = match?.[2] ?? "";
  const canonicalPath = canonicalOpenSNPath(path);
  return canonicalPath === path ? href : `${canonicalPath}${suffix}`;
}

function canonicalOpenSNPath(pathname: string): string {
  const profilePath = canonicalProfilePath(pathname);
  const normalized = profilePath.replace(/\/+$/u, "") || "/";
  if (normalized === "/friends" || normalized === "/users") return "/network";
  return profilePath;
}

function canonicalProfilePath(pathname: string): string {
  const match = pathname.replace(/\/+$/u, "").match(/^\/(?:profile\/|@)([^/]+)(\/(?:about|friends|photos|articles))?$/u);
  if (!match) return pathname;
  const identifier = decodeProfileHandle(match[1] ?? "");
  return identifier ? `/profile/${encodeURIComponent(identifier)}${match[2] ?? ""}` : pathname;
}

function absoluteUrl(url: URL, path: string): string {
  return new URL(path, url.origin).toString();
}

function absoluteOptionalUrl(url: URL, value: unknown): string | undefined {
  const path = displayText(value, "");
  return path ? absoluteUrl(url, path) : undefined;
}

function asUrl(value: string | URL, baseUrl: string | URL = "https://opensn.local"): URL {
  if (value instanceof URL) return value;
  const raw = displayText(value, "/");
  try {
    return new URL(raw);
  } catch {
    const base = baseUrl instanceof URL ? baseUrl : new URL(displayText(baseUrl, "https://opensn.local"));
    return new URL(raw.startsWith("/") ? raw : `/${raw}`, base);
  }
}

function displayText(value: unknown, fallback: string): string {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function compactJsonLd(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(compactJsonLd).filter((item) => item !== undefined);
  if (!isRecord(value)) return value === "" ? undefined : value;
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, compactJsonLd(item)]).filter(([, item]) => item !== undefined));
}

function escapeHtml(value: unknown): string {
  return String(value ?? "").replace(/[&<>"']/gu, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" })[char] ?? char);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
